import React, { useState, useEffect, useRef } from 'react';
import { Bell, Lock, CreditCard, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { obtenerFechasPago } from '../services/pagosServices';
import { verificarHijos } from '../services/matriculaService';
import { obtenerSugerencias } from '../services/chatbotService';

const Notificaciones = ({ showNotifications, setShowNotifications, passwordIsCedula, userType, transferenciaPendientes = [] }) => {
  const notificationsRef = useRef(null);
  const [tienePagoPendiente, setTienePagoPendiente] = useState(false);
  const [numeroPagosPendientes, setNumeroPagosPendientes] = useState(0);
  const [passwordEsCedula, setPasswordEsCedula] = useState(passwordIsCedula);
  const [sugerenciasImplementadas, setSugerenciasImplementadas] = useState([]);
  const [notificacionesSinLeer, setNotificacionesSinLeer] = useState([]);

  // Función para verificar pagos pendientes de todos los hijos
  const verificarPagoPendiente = async () => {
    if (userType === 'padre') {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const userId = user?.id_usuario || user?.id;
        
        if (user && userId) {
          const hijosResponse = await verificarHijos(userId);
          const hijosActivos = hijosResponse.hijos?.filter(hijo => hijo.estado_matricula === 'Activa') || [];
          
          let contadorPagosPendientes = 0;
          
          for (const hijo of hijosActivos) {
            try {
              const fechasResponse = await obtenerFechasPago(hijo.id_matricula);
              
              let requierePago = true; // Por defecto asumimos que requiere pago
              
              if (fechasResponse.success && fechasResponse.data.length > 0) {
                const fechaActual = new Date();
                fechaActual.setHours(0, 0, 0, 0);
                const ultimoRegistro = fechasResponse.data[fechasResponse.data.length - 1];
                const proximaFechaPago = ultimoRegistro ? new Date(ultimoRegistro.proxima_fecha_pago) : null;
                
                if (ultimoRegistro) {
                  // Verificar si hay un pago en proceso de verificación (transferencia pendiente)
                  const tieneTransferenciaPendiente = ultimoRegistro.tipo_pago === 'Transferencia' && 
                                                     ultimoRegistro.estado_verificacion === 'Pendiente' &&
                                                     ultimoRegistro.comprobante_pago;
                  
                  if (tieneTransferenciaPendiente) {
                    requierePago = false; // No requiere pago si hay transferencia en revisión
                  } else if (ultimoRegistro.estado_verificacion === 'Verificado') {
                    // Pago verificado - verificar si necesita próximo pago
                    requierePago = proximaFechaPago ? fechaActual >= proximaFechaPago : false;
                  } else if (ultimoRegistro.estado_verificacion === 'Rechazado') {
                    // Pago rechazado - siempre requiere pago
                    requierePago = true;
                  } else {
                    // Estado pendiente sin transferencia o matrícula nueva
                    // Verificar fecha de pago para determinar si está vencido
                    if (proximaFechaPago) {
                      requierePago = fechaActual >= proximaFechaPago;
                    } else {
                      requierePago = true;
                    }
                  }
                }
              }
              
              if (requierePago) {
                contadorPagosPendientes++;
              }
            } catch (err) {
              console.warn(`Error al verificar pagos para matrícula ${hijo.id_matricula}:`, err);
              // Si hay error, asumimos que requiere pago
              contadorPagosPendientes++;
            }
          }
          
          setNumeroPagosPendientes(contadorPagosPendientes);
          setTienePagoPendiente(contadorPagosPendientes > 0);
        }
      } catch (err) {
        console.error('Error al verificar pagos:', err);
      }
    }
  };

  // Función para verificar sugerencias implementadas
  const verificarSugerenciasImplementadas = async () => {
    try {
      const response = await obtenerSugerencias();
      if (response.success) {
        const sieteDisAtras = new Date();
        sieteDisAtras.setDate(sieteDisAtras.getDate() - 7);

        const sugerenciasNotificadas = JSON.parse(localStorage.getItem('sugerenciasNotificadas') || '[]');
        const sugerenciasPendientes = JSON.parse(localStorage.getItem('sugerenciasPendientes') || '[]');

        const sugerenciasRecientes = response.data.filter(sugerencia => {
          const fechaSugerencia = new Date(sugerencia.fecha);
          return sugerencia.estado === 'implementada' && 
                 fechaSugerencia >= sieteDisAtras &&
                 !sugerenciasNotificadas.includes(sugerencia.id_sugerencia);
        });

        if (sugerenciasRecientes.length > 0) {
          const nuevasNotificadas = [
            ...sugerenciasNotificadas,
            ...sugerenciasRecientes.map(s => s.id_sugerencia)
          ];
          localStorage.setItem('sugerenciasNotificadas', JSON.stringify(nuevasNotificadas));

          const nuevasPendientes = [
            ...sugerenciasPendientes,
            ...sugerenciasRecientes
          ];
          localStorage.setItem('sugerenciasPendientes', JSON.stringify(nuevasPendientes));
          
          setSugerenciasImplementadas(nuevasPendientes);
          setNotificacionesSinLeer(nuevasPendientes);
        } else {
          setSugerenciasImplementadas(sugerenciasPendientes);
          setNotificacionesSinLeer(sugerenciasPendientes);
        }
      }
    } catch (error) {
      console.error('Error al verificar sugerencias:', error);
    }
  };

  const marcarSugerenciasComoVistas = () => {
    setNotificacionesSinLeer([]);
  };

  useEffect(() => {
    verificarPagoPendiente();
    verificarSugerenciasImplementadas();
  }, [userType]);

  useEffect(() => {
    const handleStorageChange = () => {
      verificarPagoPendiente();
      verificarSugerenciasImplementadas();
      const newPasswordIsCedula = localStorage.getItem('passwordIsCedula');
      if (newPasswordIsCedula !== null) {
        setPasswordEsCedula(newPasswordIsCedula === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    const observer = new MutationObserver(() => {
      const newPasswordIsCedula = localStorage.getItem('passwordIsCedula');
      if (newPasswordIsCedula !== null) {
        setPasswordEsCedula(newPasswordIsCedula === 'true');
      }
      verificarPagoPendiente();
      verificarSugerenciasImplementadas();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, [userType]);

  useEffect(() => {
    if (showNotifications) {
      marcarSugerenciasComoVistas();
    } else if (notificacionesSinLeer.length === 0) {
      localStorage.setItem('sugerenciasPendientes', JSON.stringify([]));
      setSugerenciasImplementadas([]);
    }
  }, [showNotifications, notificacionesSinLeer.length]);

  const handleToggleNotifications = () => {
    if (!showNotifications) {
      verificarSugerenciasImplementadas();
    }
    setShowNotifications(!showNotifications);
  };

  const getNotificationLink = () => {
    switch(userType) {
      case 'padre':
        return '/padre/perfil-padre';
      case 'admin':
        return '/admin/perfil-admin';  
      case 'profesor':
        return '/profesor/perfil-profesor';
      default:
        return '/';
    }
  };

  const getNotificationCount = () => {
    let count = 0;
    if (passwordIsCedula) count++;
    if (tienePagoPendiente) count++;
    if (notificacionesSinLeer.length > 0) count += notificacionesSinLeer.length;
    if (userType === 'admin' && transferenciaPendientes.length > 0) count += transferenciaPendientes.length;
    return count;
  };

  return (
    <div className="relative" ref={notificationsRef}>
      <button
        onClick={handleToggleNotifications}
        className="relative text-slate-600 hover:text-slate-900 transition-colors duration-300 p-2 rounded-full hover:bg-slate-100"
      >
        <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
        {(passwordIsCedula || tienePagoPendiente || notificacionesSinLeer.length > 0 || (userType === 'admin' && transferenciaPendientes.length > 0)) && (
          <div className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.25rem] h-5 bg-red-500 text-white text-xs font-medium rounded-full px-1">
            {getNotificationCount()}
          </div>
        )}
      </button>
      {showNotifications && (
        <div className="absolute right-0 mt-3 w-[90vw] sm:w-80 bg-white rounded-2xl shadow-xl py-2 border border-slate-100 transform transition-all duration-200 ease-out max-h-[80vh] sm:max-h-[calc(100vh-6rem)] flex flex-col z-50">
          <div className="px-4 py-2 border-b border-slate-100">
            <h3 className="text-sm sm:text-base font-semibold text-slate-900">Notificaciones</h3>
          </div>
          <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300">
            {(passwordIsCedula || tienePagoPendiente || sugerenciasImplementadas.length > 0 || (userType === 'admin' && transferenciaPendientes.length > 0)) ? (
              <div>
                {passwordIsCedula && (
                  <Link
                    to={getNotificationLink()}
                    className="block px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => setShowNotifications(false)}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <Lock className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm font-medium text-slate-900">Actualización de seguridad requerida</p>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
                          Por su seguridad, cambie su contraseña actual (cédula) por una nueva
                        </p>
                      </div>
                    </div>
                  </Link>
                )}
                {tienePagoPendiente && (
                  <Link
                    to="/padre/pagos"
                    className="block px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => setShowNotifications(false)}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <CreditCard className="h-4 w-4 text-yellow-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm font-medium text-slate-900">
                          {numeroPagosPendientes === 1 ? 'Pago pendiente' : `${numeroPagosPendientes} Pagos pendientes`}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
                          {numeroPagosPendientes === 1 
                            ? 'Tiene un pago de mensualidad pendiente' 
                            : `Tiene ${numeroPagosPendientes} pagos de mensualidad pendientes`
                          }
                        </p>
                      </div>
                    </div>
                  </Link>
                )}
                {sugerenciasImplementadas.map((sugerencia) => (
                  <div
                    key={sugerencia.id_sugerencia}
                    className="block px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <MessageCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm font-medium text-slate-900">¡Nueva mejora implementada!</p>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
                          Hemos implementado una mejora en el sistema de acuerdo a la sugerencia: {sugerencia.contenido}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
                          {new Date().toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {userType === 'admin' && transferenciaPendientes.length > 0 && (
                  <Link
                    to="/admin/finanzas/mostrar-transferencias"
                    className="block px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => setShowNotifications(false)}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <CreditCard className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm font-medium text-slate-900">
                          {transferenciaPendientes.length === 1 ? 'Transferencia pendiente' : `${transferenciaPendientes.length} Transferencias pendientes`}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
                          {transferenciaPendientes.length === 1 
                            ? 'Hay una transferencia esperando revisión' 
                            : `Hay ${transferenciaPendientes.length} transferencias esperando revisión`
                          }
                        </p>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-xs sm:text-sm text-slate-500">No tienes notificaciones nuevas</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notificaciones;